import { ComponentRef, createComponent, EnvironmentInjector, Injectable, ViewContainerRef } from '@angular/core';
import { ObjectUtils } from '@cinetixx/cinetixx-ui';
import { Guid } from 'guid-typescript';
import { Observable } from 'rxjs';
import { ImageElementComponent } from '../../ui/image-element/image-element.component';
import { PriorityShapeComponent } from '../../ui/priority-shape/priority-shape.component';
import { ShapeComponent } from '../../ui/shape/shape.component';
import { DEFAULT_IMAGE_LAYER, DEFAULT_SHAPE_DATA } from '../constants';
import { AuditoriumShapeDirective } from '../directives/auditorium-shape.directive';
import { ShapeSeatTypes, ShapeTypes } from '../enums';
import { IBaseElement, IImageElement, IPolygonPoint, IPriorityShape, ISeat, IShape, IShapePayload } from '../interfaces';
import { CreatorStateService } from '../services/app-services';
import { FlattenSeat, GuidWrapper } from '../types';
import { PriorityShapeManager } from './priority-shape.manager';
import { ShapeAttributesManager } from './shape-attributes.manager';

interface IAdditionalShapeAttributes {
  dragId?: string;
  draggable?: boolean;
  showContextMenu?: boolean;
  hasError?: boolean;
  position?: 'absolute' | 'relative';
  enableHover?: boolean
  shouldCreateResizePoints?: boolean
  forcedBackgroundColor?: string,
  shapeId?: number
}

@Injectable()
export class ShapeManager {

  private static shapeCache: { [id: number]: IShape } = {};
  private static _injector: EnvironmentInjector;

  public constructor(
    private readonly injector: EnvironmentInjector
  ) {
    ShapeManager._injector = this.injector;
  }

  /**
   * IS shape a image?
   */
  public static isShapeImage(el: HTMLElement): boolean {
    return el.tagName === 'APP-IMAGE-ELEMENT';
  }

  public static getDataToSend(shape?: IShape): IShapePayload {
    const shapeType = shape?.shapeType ?? DEFAULT_SHAPE_DATA.shapeType;

    return {
      id: shape?.id ?? DEFAULT_SHAPE_DATA.id,
      name: shape?.name ?? DEFAULT_SHAPE_DATA.name,
      shapeType,
      position: shape?.position ?? DEFAULT_SHAPE_DATA.position,
      image: shape?.image ?? DEFAULT_SHAPE_DATA.image,
      imageX: shape?.imageX ?? DEFAULT_SHAPE_DATA.imageX,
      imageY: shape?.imageY ?? DEFAULT_SHAPE_DATA.imageY,
      color: shape?.color ?? DEFAULT_SHAPE_DATA.color,
      default: shape?.default ?? DEFAULT_SHAPE_DATA.default,
      shapeSeatType: shapeType?.toLowerCase() !== ShapeTypes.PLACE_SHAPE?.toLowerCase()
        ? ShapeSeatTypes.NONE
        : (shape?.shapeSeatType ?? DEFAULT_SHAPE_DATA.shapeSeatType),
      polygonPoints: shape?.polygonPoints ?? DEFAULT_SHAPE_DATA.polygonPoints,
      rectPoints: shape?.rectPoints ?? DEFAULT_SHAPE_DATA.rectPoints,
      loveSeatId: null,
      flagDefaultShape: shape?.flagDefaultShape ?? DEFAULT_SHAPE_DATA.flagDefaultShape
    };
  }

  public static createAndGetImageComponentRef(dataObject: Partial<GuidWrapper<IImageElement>>): ComponentRef<ImageElementComponent> {
    const componentRef = createComponent(ImageElementComponent, {
      environmentInjector: this._injector
    });

    if (!dataObject.guid) {
      dataObject.guid = Guid.raw();
    }

    dataObject.layer = dataObject.layer ? (dataObject.layer + DEFAULT_IMAGE_LAYER) : DEFAULT_IMAGE_LAYER;

    Object.assign(componentRef.instance, {
      dataObject: dataObject,
      draggable: false,
      showContextMenu: true,
      position: 'absolute',
      dragId: Guid.raw()
    });

    componentRef.instance.auditoriumShapeDirective.componentRef = componentRef;

    return componentRef;
  }

  public static createAndGetPriorityShapeComponentRef(dataObject: Partial<IPriorityShape>): ComponentRef<PriorityShapeComponent> {
    const componentRef = createComponent(PriorityShapeComponent, {
      environmentInjector: this._injector
    });

    Object.assign(componentRef.instance, {
      dataObject: dataObject,
      position: 'absolute',
      dragId: Guid.raw(),
      priorityDensity: CreatorStateService.showPriorityDensity,
      componentRef: componentRef
    });

    componentRef.instance.auditoriumShapeDirective.componentRef = componentRef;

    return componentRef;
  }

  public static createAndGetShapeComponentRef(
    shape: IShape,
    dataObject: Partial<GuidWrapper<ISeat | IBaseElement>>,
    additionalData?: IAdditionalShapeAttributes
  ): ComponentRef<ShapeComponent> {
    const componentRef = createComponent(ShapeComponent, {
      environmentInjector: this._injector
    });

    if (!dataObject.guid) {
      dataObject.guid = Guid.raw();
    }

    const flattenedDataObject = ObjectUtils.flat(dataObject) as GuidWrapper<FlattenSeat>;

    Object.assign(componentRef.instance, {
      shape,
      dataObject: flattenedDataObject,
      draggable: additionalData?.draggable ?? false,
      showContextMenu: additionalData?.showContextMenu,
      canBeHovered: additionalData?.enableHover ?? false,
      shouldCreateResizePoints: additionalData?.shouldCreateResizePoints ?? true,
      position: additionalData?.position,
      dragId: additionalData?.dragId ?? Guid.raw(),
      hasError: additionalData?.hasError ?? false
    });

    componentRef.instance.auditoriumShapeDirective.componentRef = componentRef;

    if (additionalData?.forcedBackgroundColor) {
      componentRef.instance.backgroundColor = additionalData.forcedBackgroundColor;
    }

    return componentRef;
  }

  public static createShapeElementSynchronously(
    shape: IShape,
    dataObject: Partial<GuidWrapper<ISeat | IBaseElement>>,
    additionalData?: IAdditionalShapeAttributes,
    template?: ViewContainerRef
  ): HTMLElement {
    if (!dataObject.guid) {
      dataObject.guid = Guid.raw();
    }

    return ShapeManager.pushToTemplateSynchronously(
      ShapeManager.createAndGetShapeComponentRef(shape, dataObject, additionalData),
      template ?? CreatorStateService.roomTemplate,
      dataObject.guid
    );
  }

  public static createShapeElement$(
    shape: IShape,
    dataObject: Partial<GuidWrapper<ISeat | IBaseElement>>,
    additionalData?: IAdditionalShapeAttributes,
    template?: ViewContainerRef
  ): Observable<HTMLElement> {
    if (!dataObject.guid) {
      dataObject.guid = Guid.raw();
    }

    return ShapeManager.pushToTemplate$(
      ShapeManager.createAndGetShapeComponentRef(
        shape,
        dataObject,
        additionalData
      ),
      template ?? CreatorStateService.roomTemplate,
      dataObject.guid
    );
  }

  public static createImageElement$(
    dataObject: Partial<GuidWrapper<IImageElement>>,
    template?: ViewContainerRef
  ): Observable<HTMLElement> {
    if (!dataObject.guid) {
      dataObject.guid = Guid.raw();
    }

    return ShapeManager.pushToTemplate$(
      ShapeManager.createAndGetImageComponentRef(dataObject),
      template ?? CreatorStateService.roomTemplate,
      dataObject.guid
    );
  }

  public static updatePriorityShape$(
    dataObject: Partial<IPriorityShape>,
    template?: ViewContainerRef
  ): Observable<HTMLElement> {
    ShapeManager.remove(PriorityShapeManager.getHTMLElement());

    return ShapeManager.pushToTemplate$(
      ShapeManager.createAndGetPriorityShapeComponentRef(dataObject),
      template ?? CreatorStateService.roomTemplate,
      Guid.raw()
    );
  }

  public static pushToTemplateSynchronously(
    componentRef: ComponentRef<ShapeComponent>,
    template: ViewContainerRef,
    guid: string
  ): HTMLElement {
    template.insert(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();
    ShapeAttributesManager.setGUID(componentRef.location.nativeElement, guid);

    return componentRef.location.nativeElement;
  }

  public static pushToTemplate$(
    componentRef: ComponentRef<ShapeComponent | ImageElementComponent | PriorityShapeComponent>,
    template: ViewContainerRef,
    guid: string
  ): Observable<HTMLElement> {
    return new Observable((observer) => {
      setTimeout(() => {
        template.insert(componentRef.hostView);
        componentRef.changeDetectorRef.detectChanges();
        ShapeAttributesManager.setGUID(componentRef.location.nativeElement, guid);
        observer.next(componentRef.location.nativeElement);
        observer.complete();
      });
    });
  }

  public static remove(element: HTMLElement): void {
    const el = AuditoriumShapeDirective.instances.find(x => x.elementRef.nativeElement === element);
    el ? el.destroy() : element?.remove();
  }

  public static renewImageElement$(
    element: HTMLElement,
    dataObject: GuidWrapper<Partial<IImageElement>>
  ): Observable<HTMLElement> {
    setTimeout(() => ShapeManager.remove(element));
    return ShapeManager.createImageElement$(
      dataObject,
      CreatorStateService.roomTemplate
    );
  }

  public static replaceShape$(
    element: HTMLElement,
    shape: IShape,
    dataObject: GuidWrapper<Partial<ISeat | IBaseElement>>,
    additionalData?: IAdditionalShapeAttributes
  ): Observable<HTMLElement> {
    setTimeout(() => ShapeManager.remove(element));

    return ShapeManager.createShapeElement$(
      shape,
      dataObject,
      additionalData,
      CreatorStateService.roomTemplate
    );
  }

  public static renewElement$(
    element: HTMLElement,
    dataObject: GuidWrapper<Partial<ISeat | IBaseElement>>,
    additionalData?: IAdditionalShapeAttributes
  ): Observable<HTMLElement> {
    const shape = CreatorStateService.shapes.find(s => s.id === (additionalData?.shapeId ?? ShapeAttributesManager.getShapeId(element)));
    setTimeout(() => ShapeManager.remove(element));

    return ShapeManager.createShapeElement$(
      shape,
      dataObject,
      additionalData,
      CreatorStateService.roomTemplate
    );
  }

  public static getObjectById(id: number): IShape {
    if (ShapeManager.shapeCache[id]) {
      return ShapeManager.shapeCache[id];
    }

    const shape = CreatorStateService.shapes.find(x => x.id === id) ?? null;
    ShapeManager.shapeCache[id] = shape;
    return shape;
  }

  public static formatPolygonFromPoints(points: { x: number, y: number }[]): string {
    return 'polygon(' + points.map(p => `${p.x}% ${p.y}%`).join(', ') + ')';
  }

  public static getShapePartsPolygonsPoints(pointsArray: IPolygonPoint[]): IPolygonPoint[][] {
    const polygonsPoints = [];
    let currentPolygon = [];

    for (const point of pointsArray.sortByKey('pointNumber')) {
      if (point.x === -1 && point.y === -1) {
        if (currentPolygon.length > 0) {
          polygonsPoints.push(currentPolygon);
          currentPolygon = [];
        }
      } else {
        currentPolygon.push({
          x: point.x,
          y: point.y,
          pointNumber: point.pointNumber,
          id: point?.id ?? null
        });
      }
    }

    if (currentPolygon?.length > 0) {
      polygonsPoints.push(currentPolygon);
    }

    return polygonsPoints;
  }
}
